"use client";

import { createStore } from "zustand/vanilla";
import type { EventSortMode, EventWhenFilter } from "@/features/events/filter-events";
import type { CuratedEntityType, CuratedSortMode } from "@/features/curated/model";

export function createHeaderStore() {
  return createStore<{
    scrolled: boolean;
    mobileOpen: boolean;
    locationOpen: boolean;
    mobileLocationOpen: boolean;
    setScrolled: (scrolled: boolean) => void;
    setMobileOpen: (mobileOpen: boolean) => void;
    setLocationOpen: (locationOpen: boolean) => void;
    setMobileLocationOpen: (mobileLocationOpen: boolean) => void;
  }>((set) => ({
    scrolled: false,
    mobileOpen: false,
    locationOpen: false,
    mobileLocationOpen: false,
    setScrolled: (scrolled) => set({ scrolled }),
    setMobileOpen: (mobileOpen) => set({ mobileOpen }),
    setLocationOpen: (locationOpen) => set({ locationOpen }),
    setMobileLocationOpen: (mobileLocationOpen) => set({ mobileLocationOpen })
  }));
}

export type LocationOption = {
  id: string;
  slug: string;
  name: string;
  state: string;
  postcode: string | null;
};

export function createLocationOptionsStore() {
  return createStore<{
    options: LocationOption[];
    status: "idle" | "loading" | "success" | "error";
    error: string | null;
    setLoading: () => void;
    setSuccess: (options: LocationOption[]) => void;
    setError: (error: string) => void;
  }>((set) => ({
    options: [],
    status: "idle",
    error: null,
    setLoading: () => set({ status: "loading", error: null }),
    setSuccess: (options) => set({ options, status: "success", error: null }),
    setError: (error) => set({ status: "error", error })
  }));
}

export function createDirectoryStore() {
  return createStore<{
    query: string;
    category: string;
    setQuery: (query: string) => void;
    setCategory: (category: string) => void;
  }>((set) => ({
    query: "",
    category: "all",
    setQuery: (query) => set({ query }),
    setCategory: (category) => set({ category })
  }));
}

export function createMediaStore() {
  return createStore<{
    sourceIndex: number;
    loaded: boolean;
    setSourceIndex: (value: number | ((current: number) => number)) => void;
    setLoaded: (loaded: boolean) => void;
  }>((set) => ({
    sourceIndex: 0,
    loaded: false,
    setSourceIndex: (value) => set((state) => ({
      sourceIndex: typeof value === "function" ? value(state.sourceIndex) : value
    })),
    setLoaded: (loaded) => set({ loaded })
  }));
}

export function createCalendarMonthStore(month: Date) {
  return createStore<{
    month: Date;
    setMonth: (month: Date) => void;
  }>((set) => ({
    month,
    setMonth: (nextMonth) => set({ month: nextMonth })
  }));
}

export function createDealsStore() {
  return createStore<{
    query: string;
    category: string;
    setQuery: (query: string) => void;
    setCategory: (category: string) => void;
  }>((set) => ({
    query: "",
    category: "all",
    setQuery: (query) => set({ query }),
    setCategory: (category) => set({ category })
  }));
}

export type LocalBuzzDirectoryView = "table" | "cards-image" | "cards-text";
export type LocalBuzzListView = "cards" | "table" | "calendar";
export const LOCAL_BUZZ_PAGE_SIZE = 12;

export function createLocalBuzzEventsStore() {
  return createStore<{
    view: LocalBuzzListView;
    suburb: string;
    category: string;
    visibleCount: number;
    setView: (view: LocalBuzzListView) => void;
    setSuburb: (suburb: string) => void;
    setCategory: (category: string) => void;
    showMore: () => void;
    reset: () => void;
  }>((set) => ({
    view: "cards",
    suburb: "all",
    category: "all",
    visibleCount: LOCAL_BUZZ_PAGE_SIZE,
    setView: (view) => set({ view }),
    setSuburb: (suburb) => set({ suburb, visibleCount: LOCAL_BUZZ_PAGE_SIZE }),
    setCategory: (category) => set({ category, visibleCount: LOCAL_BUZZ_PAGE_SIZE }),
    showMore: () => set((state) => ({ visibleCount: state.visibleCount + LOCAL_BUZZ_PAGE_SIZE })),
    reset: () => set({ suburb: "all", category: "all", visibleCount: LOCAL_BUZZ_PAGE_SIZE })
  }));
}

export function createLocalBuzzDealsStore() {
  return createStore<{
    view: LocalBuzzListView;
    suburb: string;
    type: string;
    visibleCount: number;
    setView: (view: LocalBuzzListView) => void;
    setSuburb: (suburb: string) => void;
    setType: (type: string) => void;
    showMore: () => void;
    reset: () => void;
  }>((set) => ({
    view: "cards",
    suburb: "all",
    type: "all",
    visibleCount: LOCAL_BUZZ_PAGE_SIZE,
    setView: (view) => set({ view }),
    setSuburb: (suburb) => set({ suburb, visibleCount: LOCAL_BUZZ_PAGE_SIZE }),
    setType: (type) => set({ type, visibleCount: LOCAL_BUZZ_PAGE_SIZE }),
    showMore: () => set((state) => ({ visibleCount: state.visibleCount + LOCAL_BUZZ_PAGE_SIZE })),
    reset: () => set({ suburb: "all", type: "all", visibleCount: LOCAL_BUZZ_PAGE_SIZE })
  }));
}

export function createLocalBuzzDirectoryStore() {
  return createStore<{
    view: LocalBuzzDirectoryView;
    suburb: string;
    category: string;
    query: string;
    visibleCount: number;
    setView: (view: LocalBuzzDirectoryView) => void;
    setSuburb: (suburb: string) => void;
    setCategory: (category: string) => void;
    setQuery: (query: string) => void;
    showMore: () => void;
    reset: () => void;
  }>((set) => ({
    view: "table",
    suburb: "all",
    category: "all",
    query: "",
    visibleCount: LOCAL_BUZZ_PAGE_SIZE,
    setView: (view) => set({ view }),
    setSuburb: (suburb) => set({ suburb, visibleCount: LOCAL_BUZZ_PAGE_SIZE }),
    setCategory: (category) => set({ category, visibleCount: LOCAL_BUZZ_PAGE_SIZE }),
    setQuery: (query) => set({ query, visibleCount: LOCAL_BUZZ_PAGE_SIZE }),
    showMore: () => set((state) => ({ visibleCount: state.visibleCount + LOCAL_BUZZ_PAGE_SIZE })),
    reset: () => set({ suburb: "all", category: "all", query: "", visibleCount: LOCAL_BUZZ_PAGE_SIZE })
  }));
}

export function createServicesStore() {
  return createStore<{
    activeSuburb: string | null;
    setActiveSuburb: (activeSuburb: string | null) => void;
  }>((set) => ({
    activeSuburb: null,
    setActiveSuburb: (activeSuburb) => set({ activeSuburb })
  }));
}

export function createCuratedStore() {
  return createStore<{
    activeSection: string | null;
    entityType: CuratedEntityType;
    sort: CuratedSortMode;
    setActiveSection: (activeSection: string | null) => void;
    setEntityType: (entityType: CuratedEntityType) => void;
    setSort: (sort: CuratedSortMode) => void;
  }>((set) => ({
    activeSection: null,
    entityType: "all",
    sort: "default",
    setActiveSection: (activeSection) => set({ activeSection }),
    setEntityType: (entityType) => set({ entityType }),
    setSort: (sort) => set({ sort })
  }));
}

export function createEventsStore(weekStart: Date) {
  return createStore<{
    category: string | null;
    suburb: string | null;
    kidsOnly: boolean;
    when: EventWhenFilter;
    sort: EventSortMode;
    weekStart: Date;
    currentTime: number;
    isMobileFiltersOpen: boolean;
    setCategory: (category: string | null) => void;
    setSuburb: (suburb: string | null) => void;
    setKidsOnly: (value: boolean | ((current: boolean) => boolean)) => void;
    setWhen: (when: EventWhenFilter) => void;
    setSort: (sort: EventSortMode) => void;
    setWeekStart: (weekStart: Date) => void;
    setCurrentTime: (currentTime: number) => void;
    setIsMobileFiltersOpen: (isMobileFiltersOpen: boolean) => void;
  }>((set) => ({
    category: null,
    suburb: null,
    kidsOnly: false,
    when: "all",
    sort: "upcoming",
    weekStart,
    currentTime: Date.now(),
    isMobileFiltersOpen: false,
    setCategory: (category) => set({ category }),
    setSuburb: (suburb) => set({ suburb }),
    setKidsOnly: (value) => set((state) => ({
      kidsOnly: typeof value === "function" ? value(state.kidsOnly) : value
    })),
    setWhen: (when) => set({ when }),
    setSort: (sort) => set({ sort }),
    setWeekStart: (nextWeekStart) => set({ weekStart: nextWeekStart }),
    setCurrentTime: (currentTime) => set({ currentTime }),
    setIsMobileFiltersOpen: (isMobileFiltersOpen) => set({ isMobileFiltersOpen })
  }));
}

export function createExpandedRowsStore() {
  return createStore<{
    expandedRows: Set<string>;
    toggleRow: (id: string) => void;
  }>((set) => ({
    expandedRows: new Set(),
    toggleRow: (id) => set((state) => {
      const expandedRows = new Set(state.expandedRows);
      if (expandedRows.has(id)) expandedRows.delete(id);
      else expandedRows.add(id);
      return { expandedRows };
    })
  }));
}

export function createResponsiveStore() {
  return createStore<{
    isMobile: boolean;
    setIsMobile: (isMobile: boolean) => void;
  }>((set) => ({
    isMobile: false,
    setIsMobile: (isMobile) => set({ isMobile })
  }));
}
